import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { levelFor } from "../lib/levels";
import { toast } from "sonner";
import { RefreshCcw, Sparkles } from "lucide-react";

export default function CBT() {
  const { user, setUser } = useAuth();
  const lvl = levelFor(user?.current_level || 1);
  const [thought, setThought] = useState("");
  const [reframe, setReframe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/exercise/cbt/history");
      setHistory(data.items);
    } catch (err) {
      console.error("cbt history load failed:", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!thought.trim()) return;
    setBusy(true); setReframe(null);
    try {
      const { data } = await api.post("/exercise/cbt", { thought });
      setReframe(data.reframe);
      setHistory(h => [data, ...h]);
      try {
        const { data: u } = await api.get("/auth/me");
        setUser(u);
      } catch (err) {
        console.error("user refresh failed:", err);
      }
      toast.success("+25 XP · Resilience +1");
    } catch { toast.error("Could not generate a reframe."); }
    setBusy(false);
  };

  return (
    <div className="animate-fadeUp" data-testid="cbt-page">
      <div className="text-xs font-bold uppercase tracking-[0.25em] opacity-60">Exercise</div>
      <h1 className="font-display font-black text-4xl tracking-tighter mt-2">Reframe one thought</h1>
      <p className="opacity-70 mt-2 max-w-xl">Drop a heavy thought. Your mammoth offers a kinder, more balanced lens. Try it on — keep what fits.</p>

      <div className="mt-8 rounded-3xl p-7 shadow-lg border border-[#0e203b]/5" style={{ backgroundColor: lvl.surface }}>
        <label className="text-xs font-bold uppercase tracking-widest opacity-60">Your thought</label>
        <textarea
          data-testid="cbt-thought"
          value={thought}
          onChange={e => setThought(e.target.value)}
          rows={3}
          placeholder="e.g. I'm always going to be behind."
          className="mt-2 w-full p-4 rounded-2xl border-2 border-[#0e203b]/10 focus:border-[#0e203b] outline-none font-medium bg-white resize-none"
        />
        <div className="flex justify-end mt-4">
          <button onClick={submit} disabled={busy || !thought.trim()} data-testid="cbt-submit" className="btn-pill bg-[#0e203b] text-white rounded-full px-6 h-12 font-bold disabled:opacity-40 flex items-center gap-2">
            <RefreshCcw size={16} /> {busy ? "Reframing…" : "Get a reframe"}
          </button>
        </div>

        {reframe && (
          <div className="mt-6 p-5 rounded-2xl bg-white border-2 border-[#0e203b] animate-fadeUp" data-testid="cbt-reframe">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest"><Sparkles size={14} /> Mammoth says</div>
            <div className="mt-2 text-lg font-display leading-snug">{reframe}</div>
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-8">
          <div className="font-display font-bold text-xl mb-3">Past reframes</div>
          <div className="space-y-3">
            {history.slice(0, 10).map(h => (
              <div key={h.id} className="bg-white rounded-2xl p-4 border border-[#0e203b]/5" data-testid="cbt-history-item">
                <div className="text-sm opacity-70 line-through">{h.thought}</div>
                <div className="text-sm font-semibold mt-1">{h.reframe}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
